/**
 * FormationsManager — Formation Line Visualization.
 *
 * Reads formations_v2 windows via
 * GET /api/v1/game-match-summary/formations/<code>. v2 is windowed, not
 * per-frame: each window covers one team over [start_frame, end_frame] in one
 * possession state ('in'/'out') with a detected formation and the jersey
 * number occupying each template slot. Template slots are ordered
 * line-by-line (app/utils/football/formation_templates.FORMATIONS), so slicing
 * `assignments` by the formation's digits yields the tactical lines drawn
 * here — the same grouping rule the retired per-frame v1 payload used.
 *
 * Which state shows: the possessing team's 'in' window and the other team's
 * 'out' window, decided per frame from the tracking ball's owning team
 * ('H'/'A') and the response's home_team mapping. When possession is unknown
 * (dead ball, missing label) both teams fall back to 'out', then to whatever
 * state covers the frame — a shape from the wrong state beats a flicker.
 *
 * PARITY NOTES (from the v1 port this replaces):
 * - Imperative API (toggle/ensureLoaded/draw/enabled/config) is unchanged;
 *   canvasController and telestration call it exactly as before.
 * - convertToCanvasCoords is injected, as in the original template port.
 */
import type { TrackingFrame } from './trackingLoader'

export interface FormationsWindow {
  team: number
  state: 'in' | 'out'
  start_frame: number
  end_frame: number
  formation: string
  confidence: number
  /** Jersey number per template slot; null = slot the detector left unfilled. */
  assignments: (number | null)[] | null
}

/** The slice of the pitch canvasState this manager reads. */
export interface FormationsCanvasStateView {
  frames: TrackingFrame[]
  currentFrameIndex: number
}

interface FormationsOptions {
  getGameCode: () => string | null
  getCanvasState: () => FormationsCanvasStateView
  convertToCanvasCoords: (xCm: number, yCm: number) => { x: number; y: number }
}

export class FormationsManager {
  getGameCode: () => string | null
  getCanvasState: () => FormationsCanvasStateView
  convertToCanvasCoords: (xCm: number, yCm: number) => { x: number; y: number }

  // Runtime state
  enabled = false
  windows: FormationsWindow[] = []
  homeTeam: number | null = null
  loading = false
  error: unknown = null
  isFullyLoaded = false

  // Configuration
  config = {
    lineWidth: 2,
    lineOpacity: 0.6,
    team0Color: 'rgba(255, 255, 255, 0.8)', // White for team 0
    team1Color: 'rgba(239, 68, 68, 0.6)', // Red for team 1
    debug: false,
  }

  constructor(options: FormationsOptions) {
    this.getGameCode = options.getGameCode
    this.getCanvasState = options.getCanvasState
    this.convertToCanvasCoords = options.convertToCanvasCoords
  }

  /**
   * Parse formation string into groups
   * e.g., "442" -> [4, 4, 2], "3421" -> [3, 4, 2, 1]
   * Each digit represents a horizontal line of players
   */
  private _parseFormation(formation: string): number[] {
    // Remove any non-digit characters (e.g., "442D" -> "442")
    const cleaned = formation.replace(/\D/g, '')
    return cleaned.split('').map((d) => parseInt(d, 10))
  }

  /** Toggle formations visualization on/off */
  async toggle(): Promise<void> {
    this.enabled = !this.enabled

    if (this.enabled && !this.isFullyLoaded) {
      await this.loadAll()
    } else if (!this.enabled) {
      this._hideFormationLabels()
    }

    this._redraw()
  }

  /**
   * Load all formation windows for the game. The whole-game window set is a
   * few hundred rows, so one fetch covers playback end to end.
   */
  async loadAll(): Promise<void> {
    if (this.loading || this.isFullyLoaded) return

    this.loading = true
    const gameCode = this.getGameCode()

    try {
      const url = `/api/v1/game-match-summary/formations/${gameCode}`
      const response = await fetch(url, { credentials: 'same-origin' })
      const data = await response.json()

      if (data.success && data.windows) {
        this.windows = data.windows as FormationsWindow[]
        this.homeTeam = data.metadata?.home_team ?? null
        this.isFullyLoaded = true
        if (this.config.debug) {
          console.log('[Formations] loaded', this.windows.length, 'windows, home_team:', this.homeTeam)
        }
      } else {
        console.warn('[Formations] No formations data available:', data.error)
      }
    } catch (error) {
      console.error('[Formations] Error loading formations:', error)
      this.error = error
    } finally {
      this.loading = false
    }
  }

  /**
   * Ensure formations data is loaded.
   * Resolves when data is ready (or immediately if already loaded or disabled)
   */
  async ensureLoaded(frameNumber: number): Promise<void> {
    if (!this.enabled) return Promise.resolve()
    if (this.isFullyLoaded) return Promise.resolve()

    if (this.loading) {
      // Wait for the in-flight load (with timeout)
      await new Promise<void>((resolve, reject) => {
        const maxWait = 10000
        const startTime = Date.now()
        const checkInterval = setInterval(() => {
          if (!this.loading) {
            clearInterval(checkInterval)
            resolve()
          } else if (Date.now() - startTime > maxWait) {
            clearInterval(checkInterval)
            reject(new Error('Timeout waiting for formations to load'))
          }
        }, 50)
      })
      if (this.isFullyLoaded) return Promise.resolve()
      return Promise.reject(new Error('Failed to load formations data (concurrent load failed)'))
    }

    await this.loadAll()
    if (this.isFullyLoaded) return Promise.resolve()
    return Promise.reject(new Error(`Failed to load formations data for frame ${frameNumber}`))
  }

  /**
   * Which team has the ball on this frame, as a tracking team index, or null
   * when the feed doesn't say ('', dead ball, no ball record).
   */
  private _possessingTeam(currentFrameData: TrackingFrame | null): number | null {
    const owner = currentFrameData?.ball?.ballOwner
    if (this.homeTeam === null || (owner !== 'H' && owner !== 'A')) return null
    return owner === 'H' ? this.homeTeam : 1 - this.homeTeam
  }

  /**
   * The window covering `frame` for this team, preferring `state` but
   * accepting the other rather than showing nothing between windows.
   * Windows cover only their own possession state's frames (formation
   * detector 0.3.0), so a stoppage between possessions is in none: in
   * such a gap — a later window follows — the team's last window before
   * `frame` stays on screen. Past the team's last window, nothing.
   */
  private _windowFor(team: number, frame: number, state: 'in' | 'out'): FormationsWindow | null {
    let fallback: FormationsWindow | null = null
    let last: FormationsWindow | null = null
    let later = false
    for (const w of this.windows) {
      if (w.team !== team) continue
      if (w.end_frame < frame && (!last || w.end_frame > last.end_frame)) last = w
      if (w.start_frame > frame) later = true
      if (frame < w.start_frame || frame > w.end_frame) continue
      if (w.state === state) return w
      fallback = fallback ?? w
    }
    return fallback ?? (later ? last : null)
  }

  /** Draw formation lines for a specific frame */
  draw(frameNumber: number, currentFrameData: TrackingFrame | null = null): void {
    if (!this.enabled) {
      if (this.config.debug) console.log('Formations disabled')
      return
    }

    const canvas = document.getElementById('spatial-canvas') as HTMLCanvasElement | null
    if (!canvas) {
      console.error('Canvas not found')
      return
    }
    const ctx = canvas.getContext('2d')!
    const canvasState = this.getCanvasState()

    const possessing = this._possessingTeam(currentFrameData)
    const selected: (FormationsWindow | null)[] = [0, 1].map((team) => {
      const state: 'in' | 'out' = possessing === null ? 'out' : possessing === team ? 'in' : 'out'
      return this._windowFor(team, frameNumber, state)
    })

    for (const w of selected) {
      if (w?.formation && w.assignments) {
        this._drawTeamFormation(ctx, canvasState, w.team, w.formation, w.assignments, currentFrameData)
      }
    }

    this._drawFormationLabels(selected)
  }

  /**
   * Format formation string with dashes between characters
   * Prepends "1-" for the goalkeeper (standard coaching format)
   * e.g., "442D" -> "1-4-4-2-D", "3412" -> "1-3-4-1-2"
   */
  private _formatFormation(formation: string): string {
    return '1-' + formation.split('').join('-')
  }

  /** Update formation labels in HTML element above canvas */
  private _drawFormationLabels(selected: (FormationsWindow | null)[]): void {
    const labelsContainer = document.getElementById('formation-labels')
    const team0Label = document.getElementById('formation-label-team0')
    const team1Label = document.getElementById('formation-label-team1')

    if (!labelsContainer || !team0Label || !team1Label) return

    labelsContainer.style.display = 'flex'

    // Game code is AWAY@HOME — team 0 = away, team 1 = home
    const gameCode = this.getGameCode() || ''
    const [team0Code, team1Code] = gameCode.includes('@') ? gameCode.split('@') : ['Away', 'Home']

    const labels = [team0Label, team1Label]
    const codes = [team0Code, team1Code]
    for (const team of [0, 1]) {
      const w = selected[team]
      const label = labels[team]!
      if (w?.formation) {
        label.textContent = `${codes[team]}: ${this._formatFormation(w.formation)}`
        label.style.display = 'inline'
      } else {
        label.style.display = 'none'
      }
    }
  }

  /** Hide formation labels when formations are disabled */
  _hideFormationLabels(): void {
    const labelsContainer = document.getElementById('formation-labels')
    if (labelsContainer) {
      labelsContainer.style.display = 'none'
    }
  }

  /** Draw formation lines for a single team */
  private _drawTeamFormation(
    ctx: CanvasRenderingContext2D,
    canvasState: FormationsCanvasStateView,
    teamId: number,
    formation: string,
    assignments: (number | null)[],
    currentFrameData: TrackingFrame | null = null,
  ): void {
    // Player positions: prefer the tracking frame being drawn (passed down
    // from drawFrame) over the canvasState index, which lags during playback
    const currentFrame = currentFrameData || canvasState.frames?.[canvasState.currentFrameIndex]
    if (!currentFrame || !currentFrame.players) {
      if (this.config.debug) {
        console.warn('No current frame or players data')
      }
      return
    }

    // Parse formation to get groups (each digit is a separate line)
    const groups = this._parseFormation(formation)

    // Split assignments into groups based on formation
    const color = teamId === 0 ? this.config.team0Color : this.config.team1Color
    let startIndex = 0

    for (const groupSize of groups) {
      const group = assignments.slice(startIndex, startIndex + groupSize)
      this._drawGroupLines(ctx, currentFrame, teamId, group, color)
      startIndex += groupSize
    }
  }

  /** Draw connecting lines between players in a group */
  private _drawGroupLines(
    ctx: CanvasRenderingContext2D,
    frameData: TrackingFrame,
    teamId: number,
    jerseys: (number | null)[],
    color: string,
  ): void {
    if (jerseys.length < 2) {
      return
    }

    // Get player positions for this group. Null slots and jerseys absent from
    // the frame (synthetic detector tracks, subs) simply contribute no point.
    const positions: { x: number; y: number }[] = []
    for (const jersey of jerseys) {
      if (jersey === null) continue
      const player = frameData.players.find((p) => p.team === teamId && p.jersey === jersey)
      if (player && player.x !== null && player.y !== null) {
        // Convert from field coordinates (cm) to canvas coordinates (pixels)
        positions.push(this.convertToCanvasCoords(player.x, player.y))
      }
    }

    if (positions.length < 2) {
      return
    }

    // Sort players top-to-bottom (by Y coordinate) to create formation lines
    // This prevents zig-zagging and connects players in proper field position order
    positions.sort((a, b) => a.y - b.y)

    // Draw lines connecting all players in the group
    ctx.strokeStyle = color
    ctx.lineWidth = this.config.lineWidth
    ctx.globalAlpha = this.config.lineOpacity

    const [first, ...rest] = positions
    if (first === undefined) return
    ctx.beginPath()
    ctx.moveTo(first.x, first.y)
    for (const p of rest) ctx.lineTo(p.x, p.y)
    ctx.stroke()

    ctx.globalAlpha = 1.0 // Reset alpha
  }

  /** Redraw current frame */
  private _redraw(): void {
    const canvasState = this.getCanvasState()
    if (canvasState.currentFrameIndex !== undefined && canvasState.frames) {
      const currentFrame = canvasState.frames[canvasState.currentFrameIndex]
      if (currentFrame && currentFrame.frame !== undefined) {
        this.draw(currentFrame.frame, currentFrame)
      }
    }
  }
}
